import { FileActionNativeButton } from "../file-action/file-action-controls";
import React, { useMemo } from "react";
import * as XLSX from "xlsx";
//import type { OrderLocation } from "./GoogleMapComponent"; // <-- adjust path if needed
import type { OrderLocation } from "./InfoPopup";

type OrdersExportProps = {
  /** Rows to export (e.g., [...frozenRows, ...tableRows]) */
  orders: OrderLocation[];
  fileName?: string;
  label?: string;
  className?: string;
  style?: React.CSSProperties;

  /** Optional compact icon-only variant for toolbars/headers */
  variant?: "button" | "icon";
  title?: string; // tooltip text
};

function daysAgoNumber(date?: Date | string | null): number | "" {
  if (!date) return "";
  const created = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(created.getTime())) return "";
  const c = new Date(created);
  const n = new Date();
  c.setHours(0, 0, 0, 0);
  n.setHours(0, 0, 0, 0);
  return Math.floor((n.getTime() - c.getTime()) / (1000 * 60 * 60 * 24));
}

export default function ExportOrdersExcel({
  orders,
  fileName,
  label = "Export Orders",
  className,
  style,
  variant = "button",
  title = "Export: Order ID, Customer, Days Ago, Phone Number",
}: OrdersExportProps) {
  const rows = useMemo(
    () =>
      orders.map((o) => ({
        "Order ID": o.orderId ?? "",
        Customer: o.customerName ?? "",
        "Days Ago": daysAgoNumber(o.createdAt),
        "Phone Number": o.primaryContactNbr ?? "",
        "Requested Delivery": o.requestedDate ?? "",
        "Requested By": o.requestedBy ?? "",
      })),
    [orders]
  );

  const handleClick = () => {
    if (rows.length === 0) {
      alert("No orders to export.");
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows, {
      header: ["Order ID", "Customer", "Days Ago", "Phone Number"],
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    const name = fileName ?? `Orders_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, name);
  };

  if (variant === "icon") {
    return (
      <FileActionNativeButton action="export"
        onClick={handleClick}
        disabled={rows.length === 0}
        aria-label={title}
        title={rows.length === 0 ? "Nothing to export yet" : title}
        className={className}
        style={{
          width: 36,
          height: 36,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: rows.length === 0 ? "#e5e7eb" : "#10b981",
          color: "white",
          border: "none",
          borderRadius: 6,
          cursor: rows.length === 0 ? "not-allowed" : "pointer",
          boxShadow: "0 1px 4px rgba(0,0,0,.15)",
          ...style,
        }}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path fill="currentColor" d="M4 3h10l6 6v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm10 1v5h5" />
          <path fill="currentColor" d="M8.5 10h2l1.5 2.5L13.5 10h2l-2.5 4 2.5 4h-2l-1.5-2.5L10.5 18h-2l2.5-4-2.5-4z"/>
        </svg>
      </FileActionNativeButton>
    );
  }

  return (
    <FileActionNativeButton action="export"
      onClick={handleClick}
      disabled={rows.length === 0}
      className={className}
      style={{
        backgroundColor: rows.length === 0 ? "#9aa0a6" : "#10b981",
        color: "white",
        border: "none",
        borderRadius: 6,
        padding: "6px 12px",
        cursor: rows.length === 0 ? "not-allowed" : "pointer",
        fontWeight: "bold",
        boxShadow: "0 1px 4px rgba(0,0,0,.15)",
        ...style,
      }}
      title={rows.length === 0 ? "Nothing to export yet" : title}
    >
      {label}
    </FileActionNativeButton>
  );
}
